import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';
import styles from './EPKBuilder.module.css';

const BasicInfoStep = ({ data, updateData, errors, mode }) => {
  console.log('BasicInfoStep rendering with data:', data);
  
  // Initialize with data from props
  const [formData, setFormData] = useState({
    title: data.title || 'My Electronic Press Kit',
    artistName: data.artistName || '',
    artistType: data.artistType || '',
    location: {
      city: data.location?.city || '',
      state: data.location?.state || '',
      country: data.location?.country || ''
    },
    genres: data.genres || [],
    activeSince: data.activeSince || '',
    shortBio: data.shortBio || '',
    longBio: data.longBio || ''
  });
  
  const [newGenre, setNewGenre] = useState('');
  const [shortBioChars, setShortBioChars] = useState(formData.shortBio?.length || 0);
  const [longBioChars, setLongBioChars] = useState(formData.longBio?.length || 0);
  
  // Use ref to prevent update loops
  const initialRender = useRef(true);
  const prevFormDataRef = useRef(formData);
  
  // Sync with parent component only when data actually changes
  useEffect(() => {
    // Skip the update on the initial render
    if (initialRender.current) {
      initialRender.current = false;
      prevFormDataRef.current = formData;
      return;
    }
    
    // Only update if data has actually changed
    if (JSON.stringify(prevFormDataRef.current) !== JSON.stringify(formData)) {
      console.log('BasicInfoStep: Updating parent with data', formData);
      updateData(formData);
      
      // Update ref
      prevFormDataRef.current = formData;
    }
  }, [formData, updateData]);
  
  // Only update character counts when formData changes
  useEffect(() => {
    setShortBioChars(formData.shortBio?.length || 0);
    setLongBioChars(formData.longBio?.length || 0);
  }, [formData.shortBio, formData.longBio]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      // Handle nested fields like location.city
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleAddGenre = () => {
    if (newGenre && !formData.genres.includes(newGenre)) {
      setFormData(prev => ({
        ...prev,
        genres: [...prev.genres, newGenre]
      }));
      setNewGenre('');
    }
  };
  
  const handleRemoveGenre = (genreToRemove) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.filter(genre => genre !== genreToRemove)
    }));
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && newGenre) {
      e.preventDefault();
      handleAddGenre();
    }
  };
  
  // Generate years for the "Active Since" dropdown
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 1950; year--) {
      years.push(year);
    }
    return years;
  };
  
  return (
    <div className={`${styles.formSection} ${mode === 'view' ? styles.viewMode : ''}`}>
      <h2 className={styles.sectionTitle}>Basic Information</h2>
      <p className={styles.sectionDescription}>
        Provide the essential information about yourself or your band that will appear at the top of your EPK.
      </p>
      
      <div className={styles.formGroup}>
        <label htmlFor="title">EPK Title</label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          placeholder="e.g., Official Press Kit for [Your Name]"
        />
        <span className={styles.inputHelp}>This is the title that will appear at the top of your EPK</span>
      </div>
      
      <div className={styles.formGroup}>
        <label htmlFor="artistName">Artist/Band Name *</label>
        <input
          type="text"
          id="artistName"
          name="artistName"
          value={formData.artistName}
          onChange={handleInputChange}
          placeholder="Your full artist or band name"
          required
          className={errors?.artistName ? styles.inputError : ''}
        />
        {errors?.artistName && <span className={styles.inputError}>{errors.artistName}</span>}
      </div>
      
      <div className={styles.formGroup}>
        <label htmlFor="artistType">Artist Type</label>
        <select
          id="artistType"
          name="artistType"
          value={formData.artistType}
          onChange={handleInputChange}
        >
          <option value="">Select Type</option>
          <option value="Solo Artist">Solo Artist</option>
          <option value="Band">Band</option>
          <option value="DJ">DJ</option>
          <option value="Producer">Producer</option>
          <option value="Duo">Duo</option>
          <option value="Ensemble">Ensemble</option>
          <option value="Orchestra">Orchestra</option>
          <option value="Collective">Collective</option>
        </select>
      </div>
      
      <div className={styles.formGrid}>
        <div className={styles.formGroup}>
          <label htmlFor="location.city">City</label>
          <input
            type="text"
            id="location.city"
            name="location.city"
            value={formData.location.city}
            onChange={handleInputChange}
            placeholder="e.g., Los Angeles"
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="location.state">State/Province</label>
          <input
            type="text"
            id="location.state"
            name="location.state"
            value={formData.location.state}
            onChange={handleInputChange}
            placeholder="e.g., California"
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="location.country">Country</label>
          <input
            type="text"
            id="location.country"
            name="location.country"
            value={formData.location.country}
            onChange={handleInputChange}
            placeholder="e.g., USA"
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="activeSince">Active Since</label>
          <select
            id="activeSince"
            name="activeSince"
            value={formData.activeSince}
            onChange={handleInputChange}
          >
            <option value="">Select Year</option>
            {generateYearOptions().map(year => (
              <option key={year} value={year.toString()}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      <div className={styles.formGroup}>
        <label htmlFor="genres">Genre(s)</label>
        <div className={styles.genreInput}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              id="newGenre"
              value={newGenre}
              onChange={(e) => setNewGenre(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Add a genre (e.g., Rock, Hip-Hop, Electronic)"
              style={{ flex: 1 }}
            />
            <button 
              type="button" 
              onClick={handleAddGenre} 
              className={styles.addGenreBtn}
              style={{
                padding: '8px 16px',
                background: '#E2384D',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
          
          <div className={styles.genresTags}>
            {formData.genres.map(genre => (
              <div key={genre} className={styles.genreTag}>
                {genre}
                <button type="button" onClick={() => handleRemoveGenre(genre)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className={styles.formGroup}>
        <label htmlFor="shortBio">Short Bio (300 chars max)</label>
        <textarea
          id="shortBio"
          name="shortBio"
          value={formData.shortBio}
          onChange={handleInputChange}
          maxLength={300}
          placeholder="A concise description of you or your band in 1-2 sentences"
          style={{ height: '100px' }}
        ></textarea>
        <div className={`${styles.characterCounter} ${
          shortBioChars > 250 ? styles.characterCountWarning : ''
        } ${
          shortBioChars >= 300 ? styles.characterCountError : ''
        }`}>
          {shortBioChars}/300
        </div>
      </div>
      
      <div className={styles.formGroup}>
        <label htmlFor="longBio">Full Bio (1000 chars max)</label>
        <textarea
          id="longBio"
          name="longBio"
          value={formData.longBio}
          onChange={handleInputChange}
          maxLength={1000}
          placeholder="A more detailed biography with your background, influences, and achievements"
          style={{ height: '200px' }}
        ></textarea>
        <div className={`${styles.characterCounter} ${
          longBioChars > 900 ? styles.characterCountWarning : ''
        } ${
          longBioChars >= 1000 ? styles.characterCountError : ''
        }`}>
          {longBioChars}/1000
        </div>
      </div>
    </div>
  );
};

export default BasicInfoStep;